import {
    IVideoPlayerStrategy
} from "../../video-player.strategy.interface";
import Hls, {ErrorData, Events} from "hls.js";
import {NullableString} from "../../../../../../common/types/nullable-types";


export class HlsVideoStreamingStrategy implements IVideoPlayerStrategy {


    constructor(private videoElement: HTMLVideoElement) {
        this._hlsInstance = new Hls({
            ...Hls.DefaultConfig,
            capLevelOnFPSDrop: true
        });

        this._hlsInstance.attachMedia(this.videoElement);

    }

    private _hlsInstance: Hls;
    private _src: NullableString = null;

    get isPlaying(): boolean {
        return !this.videoElement.paused;
    }

    private _loadVideoManifest(src: string): Promise<void> {
         return new Promise<void>((resolve, reject) => {
            let eventsSubscriptions: Array<() => void> = [];

            const clearEvents = () => {
                eventsSubscriptions.forEach(unsubscribe => unsubscribe());
                eventsSubscriptions = [];
            }
            const parseManifestHandler = () => {
                clearEvents();
                resolve();
            }

            const onHslError = (event: Events.ERROR, data: ErrorData) => {
                clearEvents();
                reject(data)
            };

            this._hlsInstance.on(Hls.Events.MANIFEST_PARSED, parseManifestHandler);
            eventsSubscriptions.push(() => this._hlsInstance.off(Hls.Events.MANIFEST_PARSED, parseManifestHandler));

            this._hlsInstance.on(Hls.Events.ERROR, onHslError);
            eventsSubscriptions.push(() => this._hlsInstance.off(Hls.Events.ERROR, onHslError));

            this._hlsInstance.loadSource(src);
        })
    }

    async preload(src: string): Promise<void> {
        if(this._src !== src) {
            this._src = src;
            await this._loadVideoManifest(this._src);
        } else {
            this._hlsInstance.startLoad();
        }
    }

    async play(src: string): Promise<void> {
        await this.preload(src);
        await this.videoElement.play();
    }

    async retryPlay(): Promise<void> {
        const src = this._src;
        this._src = "";
        await this.play(src);
    }

    pause() {
        if(this.isPlaying) {
            this.videoElement.pause();
        }

        this._hlsInstance.stopLoad();
    }

    async resume(): Promise<void> {
        if(this._src && !this.isPlaying) {
            await this.play(this._src)
        }
    }

    destroy() {
        this._hlsInstance.detachMedia();
        this._hlsInstance.destroy();
        this._hlsInstance = null;
        this.videoElement = null;
    }

}